import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { getDateString } from "@redotech/util/date-format";
import { ForwardedRef, forwardRef, memo, useEffect, useState } from "react";
import { Flex } from "../flex";
import { BaseRedoInputContainer } from "./base-redo-input-container";
import {
  BaseRedoInput,
  RedoInputSize,
  RedoInputState,
} from "./base-redo-text-input";
import { BaseRedoInputInfoTooltip } from "./base-redo-text-input-info-tooltip";

export interface RedoDateInputProps {
  value: Temporal.PlainDate | null;
  setValue(value: Temporal.PlainDate | null): void;
  size?: RedoInputSize;
  state?: RedoInputState;
  label?: string;
  ariaLabel?: string;
  description?: string;
  errorText?: string;
  infoTooltip?: string;
  required?: boolean;
  className?: string;
  fullWidth?: boolean;
  autoFocus?: boolean;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  name?: string;
  "data-testid"?: string;
}

export const RedoDateInput = memo(
  forwardRef(function RedoDateInput(
    {
      size = "sm",
      value,
      setValue,
      errorText,
      infoTooltip,
      label,
      ariaLabel,
      description,
      state = "default",
      className,
      required,
      fullWidth,
      autoFocus,
      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
      name,
      "data-testid": dataTestId,
    }: RedoDateInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    const { i18n, t } = useTranslation();
    const locale = i18n.language;
    const [inputText, setInputText] = useState<string>(() =>
      formatDateInputValue(value, locale),
    );

    useEffect(() => {
      setInputText(formatDateInputValue(value, locale));
    }, [locale, value]);

    const placeholder = (() => {
      switch (dateInputMaskOrder(locale).join("-")) {
        case "day-month-year":
          return t("DD-MM-YYYY", {
            context:
              "date input placeholder; input is parsed as day-month-year",
          });
        case "year-month-day":
          return t("YYYY-MM-DD", {
            context:
              "date input placeholder; input is parsed as year-month-day",
          });
        default:
          return t("MM-DD-YYYY", {
            context:
              "date input placeholder; input is parsed as month-day-year",
          });
      }
    })();

    const parseInputDate = useHandler((input: string) => {
      const result = parseDateInputText(input, inputText, locale);
      if (result.value !== undefined) {
        setValue(result.value);
      }
      setInputText(result.text);
    });

    return (
      <BaseRedoInputContainer
        className={className}
        dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
        }
        data-arbiter="RedoDateInput"
        description={description}
        errorText={errorText}
        fullWidth={fullWidth}
        idPrefix="redo-date-input"
        label={label}
        size={size}
        state={state}
      >
        <Flex align="center" dir="row" grow={1} maxW="full">
          {name !== undefined && (
            <input
              name={name}
              type="hidden"
              value={dateInputFormValue(inputText, locale)}
            />
          )}
          <BaseRedoInput
            ariaLabel={ariaLabel}
            autoFocus={autoFocus}
            dataTestId={dataTestId}
            placeholder={placeholder}
            ref={ref}
            required={required}
            setValue={parseInputDate}
            size={size}
            state={state}
            value={inputText}
          />
          {infoTooltip && (
            <BaseRedoInputInfoTooltip
              infoTooltip={infoTooltip}
              size={size}
              state={state}
            />
          )}
        </Flex>
      </BaseRedoInputContainer>
    );
  }),
);

export function formatDateInputValue(
  date: Temporal.PlainDate | null,
  locale: string,
): string {
  if (!date) {
    return "";
  }

  return getDateString(date, {
    locale,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

const LOCALE_DATE_REGEX = /^(\D*)(\d+)(\D+)(\d+)(\D+)(\d+)(\D*)$/;
const MASK_SEPARATOR = "-";
const MONTHS_IN_YEAR = 12;
const MAX_DAYS_IN_MONTH = 31;
// Days 1-3 are ambiguous while typing (could be the first digit of 10-31), so
// a single-digit day only auto-advances past this threshold.
const MAX_AMBIGUOUS_DAY = 3;
// Any leap year works for resolving the day count of a month without a year.
const LEAP_YEAR = 2000;

/** ISO date (`YYYY-MM-DD`) a named input submits; "" until the text is a full date. */
function dateInputFormValue(text: string, locale: string): string {
  const date =
    parseExactLocaleDate(text, locale) ??
    plainDateFromMask(text, dateInputMaskOrder(locale));
  return date?.toString() ?? "";
}

interface DateInputParseResult {
  text: string;
  /** undefined means "leave the current value untouched" */
  value?: Temporal.PlainDate | null;
}

/**
 * Typed input is a dashed mask (e.g. `DD-MM-YYYY`) whose field order follows
 * the locale's date display.
 */
function dateInputMaskOrder(locale: string): readonly DateField[] {
  return localeDateShape(locale).order;
}

export function parseDateInputText(
  rawInput: string,
  previousText: string,
  locale = "en",
): DateInputParseResult {
  if (!rawInput.trim()) {
    return { text: rawInput, value: null };
  }

  const localeEdit = parseLocaleDisplayEdit(rawInput, previousText, locale);
  if (localeEdit) {
    return localeEdit;
  }

  const order = dateInputMaskOrder(locale);

  // A complete date is terminal: appending would only corrupt the year (2000 ->
  // 20009), so ignore the keystroke. Deleting/replacing still edits normally.
  const appending = rawInput.length > previousText.length;
  if (appending && readMaskParts(previousText, order)) {
    return { text: previousText };
  }

  const input = addDashesToDigitsOnlyInput(rawInput, order);
  const value = plainDateFromMask(input, order);

  const deleting = rawInput.length < previousText.length;
  if (deleting) {
    return { text: rawInput, value };
  }

  const advanced = autoAdvanceMask(input, order);
  if (advanced) {
    return { text: advanced, value };
  }

  if (value === undefined) {
    const pasted = parseExactLocaleDate(input, locale);
    if (pasted) {
      return { text: input, value: pasted };
    }
  }

  return { text: input, value };
}

// Pads and clamps the field being typed and appends the next separator once
// that field can't take another digit.
function autoAdvanceMask(
  input: string,
  order: readonly DateField[],
): string | undefined {
  const parts = input.split(MASK_SEPARATOR);
  const index = parts.length - 1;
  const field = order[index];
  const text = parts[index] ?? "";
  if (
    !field ||
    index === order.length - 1 ||
    !parts.every((part) => /^\d+$/.test(part)) ||
    text.length > FIELD_LENGTH[field]
  ) {
    return undefined;
  }

  let normalized: string;
  if (field === "year") {
    if (text.length < FIELD_LENGTH.year) {
      return undefined;
    }
    normalized = text;
  } else if (field === "month") {
    const month = clamp(Number(text), 1, MONTHS_IN_YEAR);
    if (month === 1 && text.length < FIELD_LENGTH.month) {
      return undefined;
    }
    normalized = pad2(month);
  } else {
    const monthIndex = order.indexOf("month");
    const typedMonth = monthIndex < index ? parts[monthIndex] : undefined;
    const daysInMonth = typedMonth
      ? Temporal.PlainYearMonth.from({
          month: clamp(Number(typedMonth), 1, MONTHS_IN_YEAR),
          year: LEAP_YEAR,
        }).daysInMonth
      : MAX_DAYS_IN_MONTH;
    const day = clamp(Number(text), 1, daysInMonth);
    if (day <= MAX_AMBIGUOUS_DAY && text.length < FIELD_LENGTH.day) {
      return undefined;
    }
    normalized = pad2(day);
  }

  const earlier = parts.slice(0, index).map((part, partIndex) => {
    const earlierField = order[partIndex];
    if (earlierField === "year") {
      return part;
    }
    const max = earlierField === "month" ? MONTHS_IN_YEAR : MAX_DAYS_IN_MONTH;
    return pad2(clamp(Number(part), 1, max));
  });
  return [...earlier, normalized].join(MASK_SEPARATOR) + MASK_SEPARATOR;
}

// Recovers input typed or pasted without separators (e.g. "01011990") by
// slotting the digits into the mask.
function addDashesToDigitsOnlyInput(
  input: string,
  order: readonly DateField[],
): string {
  const firstLength = FIELD_LENGTH[order[0] ?? "month"];
  if (!/^\d+$/.test(input) || input.length <= firstLength) {
    return input;
  }
  const parts: string[] = [];
  let start = 0;
  for (const field of order) {
    parts.push(input.slice(start, start + FIELD_LENGTH[field]));
    start += FIELD_LENGTH[field];
  }
  return parts.filter((part) => part.length > 0).join(MASK_SEPARATOR);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function pad2(value: number): string {
  return value.toString().padStart(2, "0");
}

type DateField = "day" | "month" | "year";

const FIELD_LENGTH: Readonly<Record<DateField, number>> = {
  day: 2,
  month: 2,
  year: 4,
};

interface LocaleDateShape {
  order: readonly DateField[];
  separatorKey: string;
}

interface LocaleDateParts {
  day: string;
  month: string;
  year: string;
}

const localeDateShapes = new Map<string, LocaleDateShape>();
const MONTH_DAY_YEAR_ORDER: readonly DateField[] = ["month", "day", "year"];

function parseLocaleDisplayEdit(
  rawInput: string,
  previousText: string,
  locale: string,
): DateInputParseResult | undefined {
  if (!parseExactLocaleDate(previousText, locale)) {
    return undefined;
  }

  const edited = parseExactLocaleDate(rawInput, locale);
  if (edited) {
    return { text: rawInput, value: edited };
  }

  // Appending onto a finished locale date would grow the last field without bound.
  if (rawInput.length > previousText.length) {
    return { text: previousText };
  }

  if (!readLocaleDateParts(rawInput, locale)) {
    return parseDateInputText(rawInput, "", locale);
  }
  return { text: rawInput };
}

function parseExactLocaleDate(
  text: string,
  locale: string,
): Temporal.PlainDate | null {
  const parts = readLocaleDateParts(text, locale);
  if (!parts || parts.year.length !== FIELD_LENGTH.year) {
    return null;
  }
  try {
    const date = Temporal.PlainDate.from(
      [
        parts.year,
        parts.month.padStart(FIELD_LENGTH.month, "0"),
        parts.day.padStart(FIELD_LENGTH.day, "0"),
      ].join("-"),
    );
    return formatDateInputValue(date, locale) === text ? date : null;
  } catch {
    return null;
  }
}

function readLocaleDateParts(
  text: string,
  locale: string,
): LocaleDateParts | null {
  const match = text.match(LOCALE_DATE_REGEX);
  if (
    !match ||
    dateSeparatorKey(text) !== localeDateShape(locale).separatorKey
  ) {
    return null;
  }
  const groups = [match[2] ?? "", match[4] ?? "", match[6] ?? ""];
  const parts: LocaleDateParts = { day: "", month: "", year: "" };
  for (const [index, field] of localeDateShape(locale).order.entries()) {
    parts[field] = groups[index] ?? "";
  }
  if (!parts.day || !parts.month || !parts.year) {
    return null;
  }
  return parts;
}

function localeDateShape(locale: string): LocaleDateShape {
  const cached = localeDateShapes.get(locale);
  if (cached) {
    return cached;
  }
  const detected = detectLocaleDateShape(locale);
  localeDateShapes.set(locale, detected);
  return detected;
}

function detectLocaleDateShape(locale: string): LocaleDateShape {
  const sample = formatDateInputValue(
    Temporal.PlainDate.from("2000-11-22"),
    locale,
  );
  const separatorKey = dateSeparatorKey(sample) ?? "";
  const tokensOf = (year: number, month: number, day: number) =>
    formatDateInputValue(
      Temporal.PlainDate.from({ year, month, day }),
      locale,
    ).match(/\d+/g) ?? [];
  const base = tokensOf(2000, 11, 22);
  const changedIndex = (year: number, month: number, day: number) => {
    const next = tokensOf(year, month, day);
    return base.findIndex((token, index) => token !== next[index]);
  };
  const yearIndex = changedIndex(2001, 11, 22);
  const monthIndex = changedIndex(2000, 12, 22);
  const dayIndex = changedIndex(2000, 11, 23);
  if (
    yearIndex < 0 ||
    monthIndex < 0 ||
    dayIndex < 0 ||
    new Set([yearIndex, monthIndex, dayIndex]).size !== 3
  ) {
    return { order: MONTH_DAY_YEAR_ORDER, separatorKey };
  }
  const order: DateField[] = [];
  order[yearIndex] = "year";
  order[monthIndex] = "month";
  order[dayIndex] = "day";
  if (order.length !== 3 || order.some((field) => !field)) {
    return { order: MONTH_DAY_YEAR_ORDER, separatorKey };
  }
  return { order, separatorKey };
}

function dateSeparatorKey(text: string): string | null {
  const match = text.match(LOCALE_DATE_REGEX);
  if (!match) {
    return null;
  }
  return `${match[1]}\0${match[3]}\0${match[5]}\0${match[7]}`;
}

function readMaskParts(
  text: string,
  order: readonly DateField[],
): LocaleDateParts | null {
  const groups = text.split(MASK_SEPARATOR);
  if (groups.length !== order.length) {
    return null;
  }
  const parts: LocaleDateParts = { day: "", month: "", year: "" };
  for (const [index, field] of order.entries()) {
    const group = groups[index] ?? "";
    // A short trailing day or month could still be mid-keystroke (2026-09-2).
    const minLength =
      field === "year" || index === order.length - 1 ? FIELD_LENGTH[field] : 1;
    const valid =
      group.length >= minLength &&
      group.length <= FIELD_LENGTH[field] &&
      /^\d+$/.test(group);
    if (!valid) {
      return null;
    }
    parts[field] = group;
  }
  return parts;
}

function plainDateFromMask(
  text: string,
  order: readonly DateField[],
): Temporal.PlainDate | undefined {
  const parts = readMaskParts(text, order);
  if (!parts) {
    return undefined;
  }
  try {
    return Temporal.PlainDate.from(
      {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
      },
      { overflow: "reject" },
    );
  } catch {
    return undefined;
  }
}
